'use client';

import { useEffect, useRef, useState } from 'react';
import { usePollingTask } from '@/hooks/use-polling-task';

// 2 minutes: a deploy's version-skew banner arriving within ~2min is
// indistinguishable from ~30s in practice (rollouts take minutes), and the
// chunk-error backstop below reloads instantly on acute breakage. The old
// 30s cadence was ~5,300 fleet requests/day of pure probing.
const CHECK_INTERVAL_MS = 120_000;
const CHUNK_RELOAD_GUARD_KEY = 'skew-chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

async function fetchServedBuildId(): Promise<string | null> {
  try {
    const response = await fetch('/build-id', { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }
    const payload = (await response.json()) as { buildId?: string };
    return typeof payload.buildId === 'string' && payload.buildId && payload.buildId !== 'unknown'
      ? payload.buildId
      : null;
  } catch {
    return null;
  }
}

/**
 * Bundlers word chunk failures differently — webpack said `Loading chunk 4821
 * failed.`, Turbopack says `Failed to load chunk <url> from module <id>` — but
 * both stamp `name = 'ChunkLoadError'`, so the name is the durable signal and
 * the message patterns only cover carriers that arrive as bare strings
 * (cross-origin `window.onerror`, stringified rejections).
 */
export function isChunkLoadError(value: unknown): boolean {
  if (value instanceof Error && value.name === 'ChunkLoadError') {
    return true;
  }
  const message = value instanceof Error ? value.message : typeof value === 'string' ? value : '';
  return /ChunkLoadError|Loading chunk [^ ]* failed|Failed to load chunk \S+ from module|Failed to fetch dynamically imported module/i.test(
    message
  );
}

/**
 * Both carriers are checked: Chrome fills `error` for same-origin failures and
 * `message` for cross-origin ones, and only one of the two may be the one that
 * names the chunk.
 */
export function isChunkLoadErrorEvent(event: Pick<ErrorEvent, 'error' | 'message'>): boolean {
  return isChunkLoadError(event.error) || isChunkLoadError(event.message);
}

export type ChunkReloadState = {
  /** This page already reloaded once; storage-independent, so it holds when sessionStorage throws. */
  alreadyReloaded: boolean;
  /** Epoch ms of the last chunk reload this tab recorded, 0 if none or unreadable. */
  lastReloadAt: number;
  online: boolean;
  /** This tab carries a live call, which a reload would drop. */
  liveCall: boolean;
};

export function shouldReloadForChunkError(state: ChunkReloadState, now: number): boolean {
  if (state.alreadyReloaded) {
    return false;
  }
  // Deferred, not spent: the latch stays unset, and the `updateReady` reload
  // (which waits for the call to end) recovers the tab after the call.
  if (state.liveCall) {
    return false;
  }
  // A dropped link throws the same ChunkLoadError as a deleted chunk, and
  // reloading an offline tab replaces the whole app — softphone included —
  // with the browser's offline page, once a minute for as long as the link is
  // down. `onLine === false` is the trustworthy half of that signal, so this
  // only suppresses reloads that were going to fail anyway.
  if (!state.online) {
    return false;
  }
  // The previous reload didn't fix it — don't loop.
  return now - state.lastReloadAt >= CHUNK_RELOAD_COOLDOWN_MS;
}

let reloadedThisPage = false;
let liveCallOnThisPage = false;

/** Set by the softphone while this tab carries a live call. */
export function setChunkReloadLiveCall(live: boolean): void {
  liveCallOnThisPage = live;
}

/** Returns whether the page is now reloading. */
function reloadOnceForChunkError(): boolean {
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) || 0);
  } catch {
    // Storage unavailable (private mode, blocked storage): reloadedThisPage is
    // what bounds this to one reload per page lifetime.
  }
  const online = typeof navigator === 'undefined' || navigator.onLine !== false;
  const state = { alreadyReloaded: reloadedThisPage, lastReloadAt, online, liveCall: liveCallOnThisPage };
  if (!shouldReloadForChunkError(state, Date.now())) {
    return false;
  }
  try {
    sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // See above.
  }
  reloadedThisPage = true;
  window.location.reload();
  return true;
}

/**
 * Entry point for lazy-import call sites that catch their own failures. A
 * caught rejection never reaches `window.onunhandledrejection`, so the
 * listeners below cannot see it; a `catch` around a dynamic `import()` must
 * hand the error here to reach the reload backstop.
 *
 * This reloads immediately, like the listeners below and unlike the
 * `updateReady` banner: it does NOT go through the app shell's gate, so it
 * never releases a parked pool line, and it declines only while the softphone
 * reports a live call (see setChunkReloadLiveCall) — not for a ringing invite.
 * Prefer it only where the alternative is a surface that is already stuck.
 *
 * Returns whether the page is now reloading. `false` means the caller should
 * run its normal error handling — the value was not a chunk failure, or the
 * reload was declined (offline, mid-call, or already reloaded).
 */
export function reloadIfChunkLoadError(value: unknown): boolean {
  if (!isChunkLoadError(value)) {
    return false;
  }
  return reloadOnceForChunkError();
}

/**
 * Detects deployment version skew: this tab runs an older frontend build than
 * the one currently served. Detection is baseline-then-compare on /build-id
 * (no build-time plumbing needed), plus a backstop that reloads once when a
 * stale tab hits a 404'd lazy chunk. For the returned `updateReady` the caller
 * decides WHEN to reload — never mid-call; the chunk backstop and
 * `reloadIfChunkLoadError` reload immediately, bypassing that gate except
 * during a live conversation.
 */
export function useVersionSkewWatcher(enabled: boolean): { updateReady: boolean } {
  const [updateReady, setUpdateReady] = useState(false);
  const baselineRef = useRef<string | null>(null);

  usePollingTask(
    async () => {
      const served = await fetchServedBuildId();
      if (!served) {
        return;
      }
      if (!baselineRef.current) {
        baselineRef.current = served;
        return;
      }
      if (served !== baselineRef.current) {
        setUpdateReady(true);
      }
    },
    // Once updateReady latches there is nothing left to learn — stop probing.
    // Hidden tabs keep probing: the softphone usually lives in a background
    // tab, and Next hard-reloads a stale tab on its next in-app navigation —
    // ungated, dropping whatever call that tab carries. It has to update
    // while idle instead.
    { enabled: enabled && !updateReady, intervalMs: CHECK_INTERVAL_MS, runImmediately: true, pauseWhenHidden: false }
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onError = (event: ErrorEvent) => {
      if (isChunkLoadErrorEvent(event)) {
        reloadOnceForChunkError();
      }
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        reloadOnceForChunkError();
      }
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [enabled]);

  return { updateReady };
}
